import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../Store/Hooks";
import {
  acknowledgeNoAnswersThunk,
  acknowledgePlanGapsThunk,
  addPlanLinkThunk,
  addSectionOverlayThunk,
  confirmPlanVersionThunk,
  deletePlanVersionThunk,
  deletePlanLinkThunk,
  discardPlanVersionThunk,
  dismissPlanRejectionsThunk,
  dismissPlanToast,
  errorMessage,
  fetchMyPlanActivity,
  fetchPlanActivity,
  fetchPlanFile,
  fetchPlanOverview,
  fetchPlanStatuses,
  fileKey,
  markPlanOutcomeSeen,
  markPlanLinkOpenedThunk,
  overridePlanVersionThunk,
  publishBriefingThunk,
  regenerateSponsorViewThunk,
  restorePlanVersionThunk,
  deleteUnusedUploadThunk,
  deleteRejectionThunk,
  retryPlanUploadThunk,
  setSectionOwnerThunk,
  statusesKey,
  ALL_ACCOUNTS_KEY,
  fetchPlanVersions,
  uploadPlanFileThunk,
  uploadRejectionOf,
  verifySectionsThunk,
  withdrawPlanUploadThunk,
} from "../Store/Reducers/AccountPlanSlice";
import type { AccountPlanStatus, SectionOwner } from "../Types/AccountPlan";

// Hooks read the Account Plan slice and dispatch its thunks; they never call the
// API themselves (team rule: requests live in *Slice.ts thunks).

/** Polling while something is processing: slow down over time, pause while the
 * tab is hidden (and poll at once when it is shown again), give up after a
 * while (the page offers a refresh). */
export const POLL_STEPS_MS = [4000, 8000, 15000];
const POLL_STEP_AFTER_MS = [2 * 60_000, 10 * 60_000];
export const POLL_GIVE_UP_MS = 25 * 60_000;

export const apiErrorMessage = (err: unknown, fallback: string): string =>
  errorMessage(err, fallback);

/** An upload error as a pre-parse rejection (Phase 3), or null for any other failure. */
export const uploadRejection = uploadRejectionOf;

export const pollDelay = (elapsedMs: number): number =>
  elapsedMs < POLL_STEP_AFTER_MS[0]
    ? POLL_STEPS_MS[0]
    : elapsedMs < POLL_STEP_AFTER_MS[1]
      ? POLL_STEPS_MS[1]
      : POLL_STEPS_MS[2];

/** Calls `tick` while `active`, backing off; returns true once it has given up.
 * A new `restartKey` starts over with a fresh budget (the page's Refresh after a
 * give-up, F-49): `active` alone never flips while the plan is still processing. */
export const usePolling = (
  active: boolean,
  tick: () => void,
  restartKey = 0
): boolean => {
  const [gaveUp, setGaveUp] = useState(false);
  const tickRef = useRef(tick);
  tickRef.current = tick;
  useEffect(() => {
    setGaveUp(false);
    if (!active) return;
    const started = Date.now();
    let timer: number | undefined;
    let stopped = false;
    const schedule = () => {
      const elapsed = Date.now() - started;
      if (elapsed >= POLL_GIVE_UP_MS) {
        stopped = true;
        setGaveUp(true);
        return;
      }
      timer = window.setTimeout(() => {
        if (typeof document === "undefined" || !document.hidden)
          tickRef.current();
        schedule();
      }, pollDelay(elapsed));
    };
    schedule();
    // Hidden tabs skip their ticks; catch up the moment the tab is back, rather
    // than showing a stale step for up to a full delay.
    const onVisible = () => {
      if (!stopped && !document.hidden) tickRef.current();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [active, restartKey]);
  return gaveUp;
};

/**
 * Plan status for many accounts (My Accounts → List). `skip` makes no request
 * (Account Plans off for the org: the routes 404).
 */
export const useAccountPlanStatuses = (
  accountIds: string[],
  skip = false,
  /** "all": every account in the organization (Plans → All accounts); ids ignored. */
  scope: "mine" | "all" = "mine"
) => {
  const dispatch = useAppDispatch();
  const key = scope === "all" ? ALL_ACCOUNTS_KEY : statusesKey(accountIds);
  const ids = useMemo(
    () => (scope === "all" || !key ? [] : key.split(",")),
    [key, scope]
  );
  const entry = useAppSelector(s => s.accountPlan.statuses[key]);
  const refresh = useCallback(() => {
    // Called even for an empty portfolio: the response's `me` still carries the
    // user's persona, so the List tab doesn't fall back to the AE layout.
    void dispatch(fetchPlanStatuses({ ids, force: true, scope }));
  }, [dispatch, ids, scope]);
  useEffect(() => {
    if (skip) return;
    void dispatch(fetchPlanStatuses({ ids, force: true, scope }));
  }, [dispatch, ids, skip, scope]);
  return {
    statuses: (entry?.data?.accounts || {}) as Record<
      string,
      AccountPlanStatus
    >,
    me: entry?.data?.me || null,
    /** scope "all": the organization's accounts. */
    rows: entry?.data?.rows || null,
    loading: !entry || (entry.loading && !entry.data),
    error: entry?.error || null,
    refresh,
  };
};

/** Every version of one account's plan, for the Plans list's account panel. */
export const useAccountPlanVersions = (accountId?: string) => {
  const dispatch = useAppDispatch();
  const entry = useAppSelector(s =>
    accountId ? s.accountPlan.versions[accountId] : undefined
  );
  useEffect(() => {
    if (accountId) void dispatch(fetchPlanVersions({ accountId, force: true }));
  }, [dispatch, accountId]);
  return {
    data: entry?.data || null,
    loading: !!accountId && (!entry || (entry.loading && !entry.data)),
    error: entry?.error || null,
  };
};

/**
 * One account's plan page data. Polls while a version is processing so the
 * version list and "current" plan update without a reload.
 */
export const useAccountPlanOverview = (accountId?: string) => {
  const dispatch = useAppDispatch();
  const entry = useAppSelector(s =>
    accountId ? s.accountPlan.overview[accountId] : undefined
  );
  const load = useCallback(async () => {
    if (accountId)
      await dispatch(fetchPlanOverview({ accountId, force: true }));
  }, [dispatch, accountId]);
  useEffect(() => {
    void load();
  }, [load]);
  // A user's refresh also restarts polling, so tracking resumes after a give-up.
  const [pollRun, setPollRun] = useState(0);
  const refresh = useCallback(async () => {
    setPollRun(n => n + 1);
    await load();
  }, [load]);

  const data = entry?.data || null;
  // Keep polling until the Sponsor View is built too, not just the parse, so
  // "Open Sponsor View" / review state appear without a reload.
  const processing = !!data?.files.some(
    f => f.status === "processing" || f.sponsor_view_status === "processing"
  );
  const pollingStopped = usePolling(
    processing,
    () => {
      if (accountId) void dispatch(fetchPlanOverview({ accountId }));
    },
    pollRun
  );

  return {
    data,
    loading: !entry || (entry.loading && !entry.data),
    error: entry?.error || null,
    refresh,
    pollingStopped,
  };
};

/**
 * One plan version with parsed pages (viewer / Sponsor View). `waiting` keeps
 * polling even when the copy we hold doesn't say "processing": a read that
 * started before a regenerate can land after it and look idle.
 */
export const useAccountPlanFile = (
  accountId?: string,
  fileId?: string | null,
  waiting = false
) => {
  const dispatch = useAppDispatch();
  const key = accountId && fileId ? fileKey(accountId, fileId) : "";
  const entry = useAppSelector(s =>
    key ? s.accountPlan.files[key] : undefined
  );
  const reload = useCallback(async () => {
    if (accountId && fileId)
      await dispatch(fetchPlanFile({ accountId, fileId, force: true }));
  }, [dispatch, accountId, fileId]);
  useEffect(() => {
    void reload();
  }, [reload]);

  const file = entry?.data || null;
  const busy =
    waiting ||
    file?.status === "processing" ||
    file?.sponsor_view_status === "processing";
  usePolling(busy, () => {
    if (accountId && fileId)
      void dispatch(fetchPlanFile({ accountId, fileId }));
  });

  return {
    file,
    loading: !!key && (!entry || (entry.loading && !entry.data)),
    error: entry?.error || null,
    reload,
  };
};

/** Plan mutations, bound to dispatch. Each resolves with the API's content or
 * throws the request's error (read it with apiErrorMessage). */
export const useAccountPlanActions = () => {
  const dispatch = useAppDispatch();
  return useMemo(
    () => ({
      uploadPlanFile: (
        accountId: string,
        file: File,
        moveFrom?: string | null,
        replaceDraft?: boolean
      ) =>
        dispatch(
          uploadPlanFileThunk({ accountId, file, moveFrom, replaceDraft })
        ).unwrap(),
      /** Phase 3: dismiss the account's pre-parse rejections. */
      dismissPlanRejections: (accountId: string) =>
        dispatch(dismissPlanRejectionsThunk({ accountId })).unwrap(),
      addPlanLink: (accountId: string, url: string) =>
        dispatch(addPlanLinkThunk({ accountId, url })).unwrap(),
      deletePlanLink: (accountId: string, linkId: string) =>
        dispatch(deletePlanLinkThunk({ accountId, linkId })).unwrap(),
      markPlanLinkOpened: (accountId: string, linkId: string) =>
        dispatch(markPlanLinkOpenedThunk({ accountId, linkId })).unwrap(),
      regenerateSponsorView: (accountId: string, fileId: string) =>
        dispatch(regenerateSponsorViewThunk({ accountId, fileId })).unwrap(),
      setSectionOwner: (
        accountId: string,
        sectionId: string,
        role: SectionOwner
      ) =>
        dispatch(setSectionOwnerThunk({ accountId, sectionId, role })).unwrap(),
      verifySections: (
        accountId: string,
        fileId: string,
        sectionIds: string[],
        role: SectionOwner
      ) =>
        dispatch(
          verifySectionsThunk({ accountId, fileId, sectionIds, role })
        ).unwrap(),
      addSectionOverlay: (
        accountId: string,
        fileId: string,
        sectionId: string,
        text: string,
        role: SectionOwner
      ) =>
        dispatch(
          addSectionOverlayThunk({ accountId, fileId, sectionId, text, role })
        ).unwrap(),
      acknowledgePlanGaps: (
        accountId: string,
        fileId: string,
        acknowledged: boolean
      ) =>
        dispatch(
          acknowledgePlanGapsThunk({ accountId, fileId, acknowledged })
        ).unwrap(),
      acknowledgeNoAnswers: (
        accountId: string,
        fileId: string,
        acknowledged: boolean
      ) =>
        dispatch(
          acknowledgeNoAnswersThunk({ accountId, fileId, acknowledged })
        ).unwrap(),
      publishBriefing: (accountId: string, fileId: string) =>
        dispatch(publishBriefingThunk({ accountId, fileId })).unwrap(),
      withdrawPlanUpload: (accountId: string, fileId: string) =>
        dispatch(withdrawPlanUploadThunk({ accountId, fileId })).unwrap(),
      retryPlanUpload: (accountId: string, fileId: string) =>
        dispatch(retryPlanUploadThunk({ accountId, fileId })).unwrap(),
      confirmPlanVersion: (accountId: string, fileId: string, note?: string) =>
        dispatch(confirmPlanVersionThunk({ accountId, fileId, note })).unwrap(),
      overridePlanVersion: (
        accountId: string,
        fileId: string,
        reason: string
      ) =>
        dispatch(
          overridePlanVersionThunk({ accountId, fileId, reason })
        ).unwrap(),
      deletePlanVersion: (accountId: string, fileId: string) =>
        dispatch(deletePlanVersionThunk({ accountId, fileId })).unwrap(),
      // Confirm plan (Phase 2): "Not this file" and its Undo.
      discardPlanVersion: (accountId: string, fileId: string) =>
        dispatch(discardPlanVersionThunk({ accountId, fileId })).unwrap(),
      restorePlanVersion: (accountId: string, fileId: string) =>
        dispatch(restorePlanVersionThunk({ accountId, fileId })).unwrap(),
      deleteUnusedUpload: (accountId: string, fileId: string) =>
        dispatch(deleteUnusedUploadThunk({ accountId, fileId })).unwrap(),
      deleteRejection: (accountId: string, rejectionId: string) =>
        dispatch(deleteRejectionThunk({ accountId, rejectionId })).unwrap(),
    }),
    [dispatch]
  );
};

/** Team ledger (My Accounts → Account Plans → Activity). Refreshes every 10 s while
 * anything is in line or processing, every 60 s otherwise; paused while hidden. */
export const ACTIVITY_BUSY_MS = 10_000;
export const ACTIVITY_IDLE_MS = 60_000;

export const usePlanActivity = (scope: "mine" | "all", skip = false) => {
  const dispatch = useAppDispatch();
  const entry = useAppSelector(s => s.accountPlan.activity[scope]);
  const refresh = useCallback(() => {
    void dispatch(fetchPlanActivity({ scope, force: true }));
  }, [dispatch, scope]);
  const data = entry?.data || null;
  const busy = !!data && data.now.length > 0;
  useEffect(() => {
    if (skip) return undefined;
    refresh();
    const t = window.setInterval(
      () => {
        if (typeof document === "undefined" || !document.hidden) refresh();
      },
      busy ? ACTIVITY_BUSY_MS : ACTIVITY_IDLE_MS
    );
    return () => window.clearInterval(t);
  }, [refresh, skip, busy]);
  return {
    data,
    loading: !entry || (entry.loading && !entry.data),
    error: entry?.error || null,
    refresh,
  };
};

/** Polls my plans in flight (header chip) and turns new outcomes into pop-ups
 * (NTF-05): every 5 s while something of mine is in flight, every 60 s otherwise.
 * `kick` polls now — call it after an upload or a briefing request. */
export const MINE_BUSY_MS = 5_000;
export const MINE_IDLE_MS = 60_000;

export const useMyPlanActivity = (enabled: boolean) => {
  const dispatch = useAppDispatch();
  const active = useAppSelector(s => s.accountPlan.myActive);
  const toasts = useAppSelector(s => s.accountPlan.toasts);
  const busy = active.length > 0;
  const kick = useCallback(() => {
    void dispatch(fetchMyPlanActivity());
  }, [dispatch]);
  useEffect(() => {
    if (!enabled) return undefined;
    kick();
    const t = window.setInterval(
      () => {
        if (typeof document === "undefined" || !document.hidden) kick();
      },
      busy ? MINE_BUSY_MS : MINE_IDLE_MS
    );
    return () => window.clearInterval(t);
  }, [enabled, busy, kick]);
  const dismiss = useCallback(
    (fileId: string, event: string) =>
      dispatch(dismissPlanToast({ fileId, event })),
    [dispatch]
  );
  const markSeen = useCallback(
    (fileId: string, kind: "read" | "brief" = "read") =>
      dispatch(markPlanOutcomeSeen({ fileId, kind })),
    [dispatch]
  );
  return { active, toasts, dismiss, kick, markSeen };
};
